import {Text} from 'src/design/atoms/Text';
import {produce} from 'immer';
import React, {useState} from 'react';
import {FormField} from 'src/app/forms/FormField';
import {useGetFormDefinitionListQuery} from 'src/redux/queries/formDefinition';
import {subFormTypeOptions} from 'src/shared/constants/fieldTypeDictionary';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  useGetFormSettings,
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../hooks/useFormBuilder';

const subFormTypeField: IFormFieldV2 = {
  name: 'subFormType',
  label: 'Sub Form Type',
  type: 'select',
  description:
    'Select the type of subform to use. Internal subforms are only visible within their parent form, whereas external subforms are also accessible individually in forms lists.',
  options: subFormTypeOptions,
};

export const SubFormSettings = React.memo(() => {
  const field = useGetSelectedField();
  const formSettings = useGetFormSettings();
  const updateSelectedField = useUpdateSelectedField();
  const [formListSearchTerm] = useState<string | undefined>();

  const {
    data: formsPayload,
    isFetching: isFormsFetching,
    isLoading: isFormsLoading,
  } = useGetFormDefinitionListQuery({
    start: 0,
    length: 10000,
    search: formListSearchTerm,
    active: true,
    includeSubforms: true,
    isRestrictedAccess: false,
  });

  const isAnyFormsLoading = React.useMemo(
    () => isFormsFetching || isFormsLoading,
    [isFormsFetching, isFormsLoading]
  );

  // Exclude sensitive forms from the subform candidate list
  const forms = React.useMemo(
    () =>
      (formsPayload?.data ?? [])
        .filter(d => !d.sensitiveData)
        .map(
          d =>
            ({
              id: String(d.id),
              label: d.name,
            } as ISelectOption)
        ),
    [formsPayload]
  );

  const subFormField: IFormFieldV2 = React.useMemo(
    () => ({
      name: 'subFormDefinitionId',
      label: 'Sub Form',
      type: 'select',
      description: 'Select the form to use as a subform',
      options: forms,
      disabled: isAnyFormsLoading,
    }),
    [forms, isAnyFormsLoading]
  );

  const handleSubformTypeChange = React.useCallback(
    (name: string, value: string) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.builderOptions = {
            ...draft.builderOptions,
            subFormType: parseInt(value),
          };
        })
      );
    },
    [updateSelectedField, field]
  );

  const handleSubformDefinitionChange = React.useCallback(
    (name: string, value: string) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.builderOptions = {
            ...draft.builderOptions,
            subFormDefinitionId: parseInt(value),
          };
        })
      );
    },
    [updateSelectedField, field]
  );

  if (!field || field.type !== 'subform') return null;

  if (formSettings.sensitiveData) {
    return (
      <Text size="sm" c="dimmed">
        Sensitive forms cannot contain subforms.
      </Text>
    );
  }

  return (
    <>
      <FormField
        field={subFormTypeField}
        value={field?.builderOptions?.subFormType?.toString()}
        onChange={handleSubformTypeChange}
      />
      <FormField
        field={subFormField}
        value={field?.builderOptions?.subFormDefinitionId?.toString()}
        onChange={handleSubformDefinitionChange}
      />
    </>
  );
});
